import type { CodeBlockSuggestion, CodeBlockSuggestionError } from '../constants';
import { codeBlockSuggestionErrorSchema, codeBlockSuggestionSchema } from '../constants';

export type ParsedSuggestionItem = CodeBlockSuggestion | CodeBlockSuggestionError;

/**
 * Optional diagnostics for callers that want to surface dropped lines
 * (e.g. to Sentry as breadcrumbs). The parser stays silent by default
 * because `@aline/common` runs in both client and server, but a drift
 * in the editor's emitted-edit schema silently deletes entire edits
 * from the UI — diagnostics let the caller see when that happens.
 *
 * `parsedKeys` is the set of top-level keys present on the JSON that
 * parsed but failed both `codeBlockSuggestionSchema` and
 * `codeBlockSuggestionErrorSchema`. We intentionally do NOT pass the
 * raw line content so callers can't accidentally ship user-private
 * edit text to a third-party logging service.
 */
export interface ParseJsonlDiagnostics {
  /** Called when a line was not valid JSON at all. */
  onParseError?: (info: { error: unknown }) => void;
  /** Called when a line was valid JSON but matched neither schema. */
  onSchemaMiss?: (info: { parsedKeys: string[] }) => void;
}

/**
 * Given a JSONL string, parse it into a list of suggestions and errors.
 */
export function parseJsonlToSuggestionList(
  jsonlString: string,
  diagnostics: ParseJsonlDiagnostics = {}
): ParsedSuggestionItem[] {
  const lines = jsonlString.split('\n').filter(line => line.trim().length > 0);
  return lines.flatMap<ParsedSuggestionItem>(line => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(line);
    } catch (error) {
      diagnostics.onParseError?.({ error });
      return [];
    }
    // Try to parse as suggestion first.
    const suggestionResult = codeBlockSuggestionSchema.safeParse(parsed);
    if (suggestionResult.success) return [suggestionResult.data];

    // Try to parse as error.
    const errorResult = codeBlockSuggestionErrorSchema.safeParse(parsed);
    if (errorResult.success) return [errorResult.data];

    // Neither schema matched — surface the shape (keys only) so callers
    // can detect drift in the editor's emitted-edit schema.
    const parsedKeys =
      parsed && typeof parsed === 'object' ? Object.keys(parsed as Record<string, unknown>) : [];
    diagnostics.onSchemaMiss?.({ parsedKeys });
    return [];
  });
}
